/**
 * Popup Component
 *
 * Displays an informational popup for KEB markers using OpenLayers overlay.
 */

import Overlay from 'ol/Overlay';

/**
 * KEB type icons mapping
 */
const KEB_ICONS = {
  wiki: '\u{1F4D6}',
  'fun-fact': '\u{1F4A1}',
  disease: '\u{1F3E5}',
  medication: '\u{1F48A}',
  default: '\u{2753}'
};

/**
 * Popup class - displays KEB information
 */
export class Popup {
  /**
   * Create a new Popup
   * @param {import('../map/CellMap.js').CellMap} cellMap - The CellMap instance
   */
  constructor(cellMap) {
    this.cellMap = cellMap;
    this.map = cellMap.getMap();
    this.currentKeb = null;

    this.element = null;
    this.closeBtn = null;
    this.iconEl = null;
    this.titleEl = null;
    this.contentEl = null;
    this.linkEl = null;
    this.overlay = null;

    this.render();
    this.setupOverlay();
    this.bindEvents();
  }

  /**
   * Render the popup element
   * @private
   */
  render() {
    this.element = document.createElement('div');
    this.element.className = 'popup';
    this.element.innerHTML = `
      <button class="popup-close" aria-label="Schlie\u00DFen">&times;</button>
      <div class="popup-header">
        <span class="popup-icon"></span>
        <h4 class="popup-title"></h4>
      </div>
      <div class="popup-content"></div>
      <div class="popup-footer">
        <a class="popup-link" href="#" target="_blank" rel="noopener" hidden>
          Mehr erfahren \u2192
        </a>
      </div>
    `;

    this.closeBtn = this.element.querySelector('.popup-close');
    this.iconEl = this.element.querySelector('.popup-icon');
    this.titleEl = this.element.querySelector('.popup-title');
    this.contentEl = this.element.querySelector('.popup-content');
    this.linkEl = this.element.querySelector('.popup-link');

    // Append to body (will be positioned by overlay)
    document.body.appendChild(this.element);
  }

  /**
   * Set up the OpenLayers overlay
   * @private
   */
  setupOverlay() {
    this.overlay = new Overlay({
      element: this.element,
      autoPan: {
        animation: {
          duration: 250
        }
      },
      positioning: 'bottom-center',
      offset: [0, -15]
    });

    this.map.addOverlay(this.overlay);
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEvents() {
    // Close button
    this.closeBtn.addEventListener('click', () => this.close());

    // Escape key
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);

    // Prevent map clicks from propagating through popup
    this.element.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  /**
   * Show popup for a KEB
   * @param {Object} keb - KEB data
   * @param {Array} coordinate - [x, y] position
   */
  show(keb, coordinate) {
    this.currentKeb = keb;

    // Update content
    this.iconEl.textContent = this.getIcon(keb.type);
    this.titleEl.textContent = keb.name;
    this.contentEl.innerHTML = this.sanitizeHtml(keb.description || '');

    // Link
    if (keb.linkToMore) {
      this.linkEl.href = keb.linkToMore;
      this.linkEl.hidden = false;
    } else {
      this.linkEl.hidden = true;
    }

    // Position and show
    this.overlay.setPosition(coordinate);
    this.element.classList.add('popup--visible');
  }

  /**
   * Close the popup
   */
  close() {
    this.element.classList.remove('popup--visible');
    this.overlay.setPosition(undefined);
    this.currentKeb = null;
  }

  /**
   * Check if popup is open
   * @returns {boolean}
   */
  isOpen() {
    return this.element.classList.contains('popup--visible');
  }

  /**
   * Get current KEB
   * @returns {Object|null}
   */
  getCurrentKeb() {
    return this.currentKeb;
  }

  /**
   * Get icon for KEB type
   * @param {string} type
   * @returns {string}
   */
  getIcon(type) {
    return KEB_ICONS[type] || KEB_ICONS.default;
  }

  /**
   * Sanitize HTML to prevent XSS
   * Allows basic formatting tags but removes scripts and event handlers
   * @param {string} html
   * @returns {string}
   */
  sanitizeHtml(html) {
    if (!html) {
      return '';
    }

    const div = document.createElement('div');
    div.innerHTML = html;

    // Remove script tags
    div.querySelectorAll('script').forEach((el) => el.remove());

    // Remove style tags
    div.querySelectorAll('style').forEach((el) => el.remove());

    // Remove event handlers from all elements
    div.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
        // Also remove javascript: URLs
        if (
          attr.name === 'href' &&
          attr.value.toLowerCase().startsWith('javascript:')
        ) {
          el.removeAttribute(attr.name);
        }
      });
    });

    return div.innerHTML;
  }

  /**
   * Get the popup element
   * @returns {HTMLElement}
   */
  getElement() {
    return this.element;
  }

  /**
   * Get the overlay
   * @returns {Overlay}
   */
  getOverlay() {
    return this.overlay;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    document.removeEventListener('keydown', this.escapeHandler);
    this.map.removeOverlay(this.overlay);
    this.element.remove();
  }
}
